import * as React from 'react';
import {
    Checkbox,
    DefaultButton,
    MessageBar,
    MessageBarType,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
} from '@fluentui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiHelper } from '../../../ApiHelper';
import {
    AgencyLogDisplayEntry,
    AgencyLogDisplayGroup,
    AgencyLogEntryCategory,
    LOG_ENTRY_FILTER_OPTIONS,
    buildLogDisplayGroups,
    getItemText,
    getItemType,
    isAgencyJobLogs,
} from '../agencyLogs';
import type { AgencyJobLogs } from '../types';
import {
    AuthenticatedDownloadLink,
    PageShell,
    buildAgencyLogsDownloadUrl,
    formatDate,
    useDocumentTitle,
} from '../shared';

const cardStyle: React.CSSProperties = {
    border: '1px solid #e1dfdd',
    borderRadius: 6,
    padding: 12,
    background: '#ffffff',
    minWidth: 0,
};

const preStyle: React.CSSProperties = {
    margin: 0,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    fontFamily: 'Consolas, "Courier New", monospace',
    fontSize: 12,
};

export function AgencyLogsPage() {
    useDocumentTitle('Agency logs');

    const navigate = useNavigate();
    const { serviceName, taskListId, taskId, workItemId } = useParams();
    const [logs, setLogs] = React.useState<AgencyJobLogs | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [filters, setFilters] = React.useState<Set<AgencyLogEntryCategory>>(
        () => new Set(LOG_ENTRY_FILTER_OPTIONS.map(option => option.key)),
    );

    const ids = React.useMemo(
        () => parseArtifactIds(taskListId, taskId, workItemId),
        [taskId, taskListId, workItemId],
    );
    const downloadUrl = serviceName && ids
        ? buildAgencyLogsDownloadUrl(serviceName, ids.taskListId, ids.taskId, ids.workItemId)
        : undefined;

    React.useEffect(() => {
        if (!downloadUrl) {
            setError('The Agency log route is missing required identifiers.');
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);
        ApiHelper<unknown>(downloadUrl)
            .then(response => {
                if (!isAgencyJobLogs(response)) {
                    throw new Error('The stored Agency log does not contain a chatHistory array.');
                }
                setLogs(response);
            })
            .catch(err => setError(err instanceof Error ? err.message : 'Failed to load Agency logs.'))
            .finally(() => setLoading(false));
    }, [downloadUrl]);

    const groups = React.useMemo(() => logs ? buildLogDisplayGroups(logs) : [], [logs]);
    const visibleGroups = React.useMemo(() => groups
        .map(group => ({ ...group, entries: group.entries.filter(entry => filters.has(entry.category)) }))
        .filter(group => group.entries.length > 0), [filters, groups]);

    return (
        <PageShell title="Agency logs">
            <Stack horizontal wrap tokens={{ childrenGap: 8 }} verticalAlign="center">
                <DefaultButton text="Back" onClick={() => navigate(-1)} />
                {downloadUrl && (
                    <AuthenticatedDownloadLink
                        url={downloadUrl}
                        fallbackFileName={`workitem-${ids?.workItemId}-agency-logs.json`}
                        fallbackErrorMessage="Failed to download Agency logs."
                    >
                        Download raw JSON
                    </AuthenticatedDownloadLink>
                )}
            </Stack>

            {logs && (
                <Stack horizontal wrap tokens={{ childrenGap: 16 }}>
                    <Text><strong>Job:</strong> {String(logs.id ?? 'Unknown')}</Text>
                    <Text><strong>Created:</strong> {formatOptionalDate(logs.createdAt)}</Text>
                    <Text><strong>Updated:</strong> {formatOptionalDate(logs.updatedAt)}</Text>
                </Stack>
            )}

            <Stack horizontal wrap tokens={{ childrenGap: 12 }}>
                {LOG_ENTRY_FILTER_OPTIONS.map(option => (
                    <Checkbox
                        key={option.key}
                        label={option.text}
                        checked={filters.has(option.key)}
                        onChange={(_, checked) => {
                            setFilters(current => {
                                const next = new Set(current);
                                if (checked) next.add(option.key);
                                else next.delete(option.key);
                                return next;
                            });
                        }}
                    />
                ))}
            </Stack>

            {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
            {loading && <Spinner size={SpinnerSize.large} label="Loading Agency logs..." />}
            {!loading && !error && visibleGroups.length === 0 && (
                <MessageBar>No log entries match the selected filters.</MessageBar>
            )}
            {!loading && !error && visibleGroups.map(group => (
                <LogGroup key={group.key} group={group} />
            ))}
            {filters.has('diff') && logs && Array.isArray(logs.toolDiffs) && logs.toolDiffs.length > 0 && (
                <Expandable title={`Tool diffs (${logs.toolDiffs.length})`} initialOpen={false}>
                    <pre style={preStyle}>{formatJson(logs.toolDiffs)}</pre>
                </Expandable>
            )}
        </PageShell>
    );
}

function LogGroup({ group }: { group: AgencyLogDisplayGroup }) {
    return (
        <Expandable title={group.label} initialOpen={group.completion || group.key === 'transcript'}>
            <Stack tokens={{ childrenGap: 10 }}>
                {group.entries.map(entry => <LogEntry key={entry.key} entry={entry} />)}
            </Stack>
        </Expandable>
    );
}

function LogEntry({ entry }: { entry: AgencyLogDisplayEntry }) {
    const type = getItemType(entry.item);
    const text = getItemText(entry.item);

    if (entry.category === 'assistant' && text !== undefined) {
        if (entry.completion) {
            return (
                <Expandable title="Completion message" initialOpen>
                    <pre style={preStyle}>{text}</pre>
                </Expandable>
            );
        }
        return <div style={cardStyle}><pre style={preStyle}>{text}</pre></div>;
    }

    if (entry.category === 'prompt' && text !== undefined) {
        return (
            <Expandable title={`${capitalize(entry.role)} prompt`} initialOpen={entry.role === 'user'}>
                <pre style={preStyle}>{text}</pre>
            </Expandable>
        );
    }

    if (type === 'FunctionCallContent') {
        const name = typeof entry.item.functionName === 'string' ? entry.item.functionName : 'Tool call';
        return (
            <Expandable title={name} initialOpen={false}>
                <Stack tokens={{ childrenGap: 8 }}>
                    <JsonBlock label="Arguments" value={entry.item.arguments} />
                    {entry.result && <JsonBlock label="Result" value={entry.result.result ?? entry.result} />}
                </Stack>
            </Expandable>
        );
    }

    if (type === 'FunctionResultContent') {
        return (
            <Expandable title="Unmatched tool result" initialOpen={false}>
                <JsonBlock value={entry.item.result ?? entry.item} />
            </Expandable>
        );
    }

    return (
        <Expandable title={`${capitalize(entry.role)} - ${type}`} initialOpen={false}>
            <JsonBlock value={entry.item} />
        </Expandable>
    );
}

function JsonBlock({ label, value }: { label?: string; value: unknown }) {
    return (
        <div>
            {label && <Text variant="smallPlus"><strong>{label}</strong></Text>}
            <pre style={preStyle}>{formatJson(value)}</pre>
        </div>
    );
}

function Expandable({
    title,
    initialOpen,
    children,
}: {
    title: string;
    initialOpen: boolean;
    children: React.ReactNode;
}) {
    const [open, setOpen] = React.useState(initialOpen);
    return (
        <details
            open={open}
            onToggle={event => setOpen(event.currentTarget.open)}
            style={cardStyle}
        >
            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{title}</summary>
            <div style={{ marginTop: 10 }}>{children}</div>
        </details>
    );
}

function parseArtifactIds(taskListId?: string, taskId?: string, workItemId?: string) {
    const parsed = [taskListId, taskId, workItemId].map(value => Number.parseInt(value ?? '', 10));
    if (parsed.some(value => !Number.isInteger(value) || value <= 0)) {
        return undefined;
    }
    return { taskListId: parsed[0], taskId: parsed[1], workItemId: parsed[2] };
}

function formatOptionalDate(value: unknown) {
    return typeof value === 'string' && value.trim() ? formatDate(value) : 'Unknown';
}

function formatJson(value: unknown) {
    if (value === undefined) return 'No data';
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
}

function capitalize(value: string) {
    return value ? value[0].toUpperCase() + value.slice(1) : 'Unknown';
}
